import React, { useEffect, useState } from 'react'
import { useAuth } from "../api/authStore"
import moment from 'moment'
import humanFormat from 'human-format'
import LoadingSpinner from '../components/LoadingSpinner'

const ProxyInfo = (props) => {

    const { user, getAccessTokenSilently, setToken, logout } = useAuth()
    const [proxyId, setProxyId] = useState(null)
    const [error, setError] = useState(null)
    const [open, setOpen] = useState(false)
    const [proxyInfo, setProxyInfo] = useState({})
    const [isCheckingSpeed, setIsCheckingSpeed] = useState(false)
    const [proxySpeed, setProxySpeed] = useState(null)
    const [lastSpeedResult, setLastSpeedResult] = useState(null)
    const [isLoadingLastSpeed, setIsLoadingLastSpeed] = useState(false)

    // Функция для вычисления возраста прокси
    const calculateProxyAge = (firstSeen) => {
        if (!firstSeen) return 'N/A'
        
        const now = moment()
        const firstSeenDate = moment(firstSeen)
        
        const years = now.diff(firstSeenDate, 'years')
        if (years >= 1) return `${years}y`
        
        const months = now.diff(firstSeenDate, 'months')
        if (months >= 1) return `${months}m`
        
        const days = now.diff(firstSeenDate, 'days')
        if (days >= 1) return `${days}d`
        
        const hours = now.diff(firstSeenDate, 'hours')
        return `${hours}h`
    }

    const handleChangeProxyId = (id) => {
        setProxyId(id)
    }

    const handleGetInfo = async(proxyIdToSearch = proxyId) => {
        if (!proxyIdToSearch) return
        
        try {
            setError(null) // Очищаем предыдущие ошибки
            setProxySpeed(null) // Сбрасываем скорость при загрузке нового прокси
            setLastSpeedResult(null) // Сбрасываем последние результаты при загрузке нового прокси
            
            // Если это объект, используем значение из поля ввода
            let idToSend
            if (typeof proxyIdToSearch === 'object') {
                idToSend = String(proxyId).trim()
            } else {
                idToSend = String(proxyIdToSearch).trim()
            }
            
            // Проверяем, что это не пустая строка
            if (!idToSend || idToSend === '[object Object]') {
                setError('Invalid Proxy ID format')
                return
            }
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).getLeaseInfo(idToSend)
            setProxyInfo(response)
        } catch (e) {
            setError(JSON.stringify(e))
        }
    }

    // Автоматическая загрузка при получении selectedProxyId
    useEffect(() => {
        if (props.selectedProxyId && props.selectedProxyId !== proxyId) {
            setProxyId(props.selectedProxyId)
            handleGetInfo(props.selectedProxyId)
        }
    }, [props.selectedProxyId])

    // Функция тестирования скорости
    const checkSpeed = async () => {
        if (!proxyInfo?.agentId && !proxyInfo?.agent) {
            setError('Agent ID not found in proxy data')
            return
        }
        
        try {
            setIsCheckingSpeed(true)
            setError(null)
            
            // Используем Agent ID для тестирования скорости
            const agentId = proxyInfo.agentId || proxyInfo.agent
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).checkProxySpeed(agentId)
            
            if (response?.speed && !isNaN(response.speed) && response.speed > 0) {
                const speed = (response.speed / 1000 / 1000).toFixed(2)
                setProxySpeed(speed)
            } else {
                setError('Invalid speed test result')
                setProxySpeed(null)
            }
        } catch (e) {
            // Обрабатываем разные типы ошибок
            if (e?.error === 'agentNotFound') {
                setError('Agent not found or not available for speed test')
            } else if (e?.response?.data?.error === 'agentNotFound') {
                setError('Agent not found or not available for speed test')
            } else {
                setError('Failed to check proxy speed')
            }
            
            setProxySpeed(null)
        } finally {
            setIsCheckingSpeed(false)
        }
    }

    // Функция получения последних результатов проверки скорости
    const getLastSpeedResult = async () => {
        if (!proxyInfo?.agentId && !proxyInfo?.agent) {
            setError('Agent ID not found in proxy data')
            return
        }
        
        try {
            setIsLoadingLastSpeed(true)
            setError(null)
            
            // Используем Agent ID для получения последних результатов
            const agentId = proxyInfo.agentId || proxyInfo.agent
            
            const response = await props.apiClient.setToken(getAccessTokenSilently()).getAgentInfo(agentId)
            
            // Данные о скорости находятся в объекте speed
            const speedData = response?.speed?.speed
            const checkTime = response?.speed?.checkTime
            
            if (speedData && !isNaN(speedData) && speedData > 0) {
                const speed = (speedData / 1000 / 1000).toFixed(2)
                setLastSpeedResult({
                    speed: speed,
                    checkTime: checkTime
                })
            } else {
                setError('No previous speed test results found')
                setLastSpeedResult(null)
            }
        } catch (e) {
            // Обрабатываем разные типы ошибок
            if (e?.error === 'agentNotFound') {
                setError('Agent not found or no speed test results available')
            } else if (e?.response?.data?.error === 'agentNotFound') {
                setError('Agent not found or no speed test results available')
            } else {
                setError('Failed to get last speed test result')
            }
            
            setLastSpeedResult(null)
        } finally {
            setIsLoadingLastSpeed(false)
        }
    }


    return (
        <div style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-xl)',
            alignItems: 'center'
        }}>
            
            {/* Поиск прокси */}
            <div style={{
                display: 'flex',
                gap: 'var(--spacing-md)',
                alignItems: 'center',
                justifyContent: 'center',
                flexWrap: 'wrap'
            }}>
                <div style={{ position: 'relative' }}>
                    <input
                        type="text"
                        placeholder="Enter proxy ID"
                        value={proxyId || ''}
                        onChange={(e) => handleChangeProxyId(e.target.value)}
                        className="form-input"
                        style={{ minWidth: '250px' }}
                    />
                </div>
                <button
                    onClick={handleGetInfo}
                    className="btn btn-primary"
                    style={{ minWidth: '120px' }}
                >
                    Get Info
                </button>
            </div>

            {/* Ошибка */}
            {error && (
                <div className="form-error" style={{
                    textAlign: 'center',
                    maxWidth: '400px'
                }}>
                    {error}
                </div>
            )}
            {/* Информация о прокси */}
            {Object.keys(proxyInfo).length > 0 && (
                <div style={{
                    width: '100%',
                    maxWidth: '500px',
                    margin: '0 auto'
                }}>
                    
                    {/* Информация о прокси */}
                    <div className="form-card">
                        <h3 style={{
                            color: 'var(--text-light)',
                            fontSize: 'var(--text-lg)',
                            fontWeight: '600',
                            marginBottom: 'var(--spacing-lg)',
                            textAlign: 'center',
                            borderBottom: '1px solid var(--light-gray)',
                            paddingBottom: 'var(--spacing-md)'
                        }}>
                            🔧 Proxy Information
                        </h3>
                        
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-md)'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Proxy ID:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>
                                    {(proxyInfo?.info?.firstSeen || proxyInfo?.currentInfo?.firstSeen || proxyInfo?.info?.probeActv !== undefined || proxyInfo?.currentInfo?.probeActv !== undefined || proxyInfo?.failPct !== undefined || proxyInfo?.info?.failPct !== undefined || proxyInfo?.currentInfo?.failPct !== undefined || proxyInfo?.stats?.failPct !== undefined) && (
                                        <span style={{
                                            color: 'var(--primary-orange)',
                                            fontWeight: '600'
                                        }}>
                                            {(() => {
                                                const failValue = proxyInfo?.failPct !== undefined && proxyInfo?.failPct !== null ? proxyInfo.failPct : 
                                                                  proxyInfo?.info?.failPct !== undefined && proxyInfo?.info?.failPct !== null ? proxyInfo.info.failPct : 
                                                                  proxyInfo?.currentInfo?.failPct !== undefined && proxyInfo?.currentInfo?.failPct !== null ? proxyInfo.currentInfo.failPct : 
                                                                  proxyInfo?.stats?.failPct !== undefined && proxyInfo?.stats?.failPct !== null ? proxyInfo.stats.failPct : null;
                                                
                                                if (failValue === null) {
                                                    return <span style={{ color: '#6c757d' }}>N/A</span>;
                                                }
                                                
                                                const failColor = failValue === 0 ? '#28a745' : '#dc3545';
                                                return (
                                                    <span style={{ color: failColor }}>
                                                        {failValue.toFixed(3)}%
                                                    </span>
                                                );
                                            })()}{proxyInfo?.failPct !== undefined || proxyInfo?.info?.failPct !== undefined || proxyInfo?.currentInfo?.failPct !== undefined || proxyInfo?.stats?.failPct !== undefined ? ' ' : ''}
                                            {calculateProxyAge(proxyInfo?.info?.firstSeen || proxyInfo?.currentInfo?.firstSeen)}/
                                            {Math.round((proxyInfo?.info?.probeActv || proxyInfo?.currentInfo?.probeActv || 0) * 100)}%{' '}
                                        </span>
                                    )}
                                    <span style={{
                                        fontFamily: 'var(--font-mono)'
                                    }}>{proxyInfo?.id}</span>
                                </span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Speed:</span>
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--spacing-sm)'
                                }}>
                                    {/* Кнопка "Get Last" - показывает последний результат */}
                                    <button
                                        className="btn btn-outline btn-small"
                                        onClick={getLastSpeedResult}
                                        disabled={isLoadingLastSpeed}
                                        title={lastSpeedResult ? moment(lastSpeedResult.checkTime).fromNow() : ''}
                                        style={{
                                            minWidth: '100px',
                                            opacity: isLoadingLastSpeed ? 0.7 : 1
                                        }}
                                    >
                                        {isLoadingLastSpeed ? 'Loading...' : 
                                         lastSpeedResult ? `${lastSpeedResult.speed} Mbps` : 
                                         'Get Last'}
                                    </button>
                                    
                                    {/* Кнопка "Test Speed" - запускает новую проверку */}
                                    <button
                                        className="btn btn-outline btn-small"
                                        onClick={checkSpeed}
                                        disabled={isCheckingSpeed}
                                        title={proxySpeed ? moment().fromNow() : ''}
                                        style={{
                                            minWidth: '100px',
                                            minHeight: '32px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            opacity: isCheckingSpeed ? 0.7 : 1
                                        }}
                                    >
                                        {isCheckingSpeed ? (
                                            <LoadingSpinner size={22} />
                                        ) : proxySpeed ? (
                                            `${proxySpeed} Mbps`
                                        ) : (
                                            'Test Speed'
                                        )}
                                    </button>
                                </div>
                            </div>


                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>IP Address:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontFamily: 'var(--font-mono)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.ip || proxyInfo?.currentInfo?.ip || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>User ID:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.user}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Country:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.loc?.cc || proxyInfo?.currentInfo?.loc?.cc || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Region:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.loc?.reg || proxyInfo?.currentInfo?.loc?.reg || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>City:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.loc?.city || proxyInfo?.currentInfo?.loc?.city || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>ZIP:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.loc?.zip || proxyInfo?.currentInfo?.loc?.zip || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>ISP:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.info?.loc?.isp || proxyInfo?.currentInfo?.loc?.isp || 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Refunded:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.refunded || 'No'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Start Date:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.start ? moment(proxyInfo.start).format('MMM-D H:m, YYYY') : 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Expire Date:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.until ? moment(proxyInfo.until).format('MMM-D H:m, YYYY') : 'N/A'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Traffic IN:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.stats?.inBytes > 0 ? humanFormat.bytes(proxyInfo.stats.inBytes, {decimals: 0}) : '0'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Traffic OUT:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.stats?.outBytes > 0 ? humanFormat.bytes(proxyInfo.stats.outBytes, {decimals: 0}) : '0'}</span>
                            </div>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-xs)',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Authorization by IP:</span>
                                <pre style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-xs)',
                                    fontFamily: 'var(--font-mono)',
                                    margin: '0',
                                    background: 'var(--dark-gray)',
                                    padding: 'var(--spacing-xs)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {proxyInfo?.authPort ? JSON.stringify(proxyInfo.authPort, null, 2) : 'N/A'}
                                </pre>
                            </div>

                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--spacing-xs)',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Authorization by Username:</span>
                                <pre style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-xs)',
                                    fontFamily: 'var(--font-mono)',
                                    margin: '0',
                                    background: 'var(--dark-gray)',
                                    padding: 'var(--spacing-xs)',
                                    borderRadius: 'var(--radius-sm)',
                                    border: '1px solid var(--light-gray)',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {proxyInfo?.socks ? JSON.stringify(proxyInfo.socks, null, 2) : 'N/A'}
                                </pre>
                            </div>

                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Connection Type:</span>
                                <span style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600',
                                    textTransform: 'capitalize'
                                }}>{proxyInfo?.info?.conn || proxyInfo?.currentInfo?.conn || 'N/A'}</span>
                            </div>

                        </div>

                        {/* Дополнительная информация */}
                        <div style={{
                            marginTop: 'var(--spacing-lg)',
                            paddingTop: 'var(--spacing-lg)',
                            borderTop: '1px solid var(--light-gray)',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-md)'
                        }}>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Price:</span>
                                <span style={{
                                    color: 'var(--primary-orange)',
                                    fontSize: 'var(--text-lg)',
                                    fontWeight: '700'
                                }}>${proxyInfo?.purchasePriceC ? (proxyInfo.purchasePriceC/100).toFixed(2) : 'N/A'}</span>
                            </div>
                            
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                padding: 'var(--spacing-sm) 0',
                                borderBottom: '1px solid var(--dark-gray)'
                            }}>
                                <span style={{
                                    color: 'var(--text-muted)',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '500'
                                }}>Status:</span>
                                <span style={{
                                    color: proxyInfo?.ended ? '#dc3545' : '#28a745',
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: '600'
                                }}>{proxyInfo?.ended ? 'Expired' : 'Active'}</span>
                            </div>
                        </div>

                        {/* Кнопка Show Details */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--spacing-md)',
                            marginTop: 'var(--spacing-lg)',
                            paddingTop: 'var(--spacing-lg)',
                            borderTop: '1px solid var(--light-gray)'
                        }}>
                            <button
                                    onClick={() => setOpen(!open)}
                                className="btn btn-outline"
                            >
                                {open ? '📖 Hide Details' : '📖 Show Details'}
                            </button>
                        </div>

                        {/* Полная информация */}
                        {open && (
                            <div style={{
                                marginTop: 'var(--spacing-lg)',
                                padding: 'var(--spacing-lg)',
                                background: 'var(--dark-gray)',
                                border: '1px solid var(--light-gray)',
                                borderRadius: 'var(--radius-md)',
                                overflow: 'auto',
                                maxHeight: '400px'
                            }}>
                                <pre style={{
                                    color: 'var(--text-light)',
                                    fontSize: 'var(--text-xs)',
                                    fontFamily: 'var(--font-mono)',
                                    margin: '0',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                        {JSON.stringify(proxyInfo, null, 2)}
                                </pre>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default ProxyInfo